import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertCircle, Copy, CheckCircle, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { AppStoreBadges } from '../components/landing/AppStoreBadges';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvitePage() {
  const { inviteToken } = useParams();
  const [dispute, setDispute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const loadInvite = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/disputes/invite/${inviteToken}`);
      setDispute(data);
    } catch {
      setError('This invite link is invalid or has expired.');
    } finally {
      setLoading(false);
    }
  }, [inviteToken]);

  useEffect(() => { loadInvite(); }, [loadInvite]);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(inviteToken);
      setCopied(true);
      toast.success('Invite code copied');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Could not copy — please copy the code manually');
    }
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center" data-testid="invite-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12" data-testid="invite-page">
      <Card className="max-w-lg w-full rounded-2xl shadow-md border" data-testid="invite-card">
        <CardContent className="p-8 text-center">
          {error ? (
            <>
              <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h2 className="font-serif text-xl font-bold mb-2">Invalid Invite</h2>
              <p className="text-muted-foreground text-sm mb-6">{error}</p>
              <Link to="/">
                <Button className="rounded-full">Go Home</Button>
              </Link>
            </>
          ) : (
            <>
              <div className="h-16 w-16 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto mb-4">
                <Smartphone className="h-8 w-8 text-primary" />
              </div>
              <h2 className="font-serif text-2xl font-bold mb-2" data-testid="invite-title">
                You&apos;ve Been Invited
              </h2>
              <p className="text-muted-foreground text-sm mb-1">
                <strong>{dispute.party_a_name}</strong> wants your side on:
              </p>
              <p className="font-serif font-bold text-lg mb-6" data-testid="invite-dispute-title">
                &ldquo;{dispute.title}&rdquo;
              </p>

              <div className="rounded-2xl bg-muted/40 border border-dashed p-5 mb-6 text-left">
                <p className="text-xs uppercase tracking-[0.15em] font-semibold text-muted-foreground mb-2">
                  Your Invite Code
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm font-mono bg-background border rounded-lg px-3 py-2 truncate" data-testid="invite-code-value">
                    {inviteToken}
                  </code>
                  <Button size="icon" variant="outline" className="rounded-lg shrink-0" onClick={copyCode} data-testid="invite-copy-btn">
                    {copied ? <CheckCircle className="h-4 w-4 text-secondary" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Download the VerdictIQ app, then paste this code inside the app to respond.
                </p>
              </div>

              <div className="flex justify-center">
                <AppStoreBadges idSuffix="invite" size="sm" />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
